import React, { useState } from "react";
import { FieldValues, useController } from "react-hook-form";

import RedactedWrapper from "lib/core_components/RedactedWrapper";
import { useDebouncedValue } from "lib/debounce";
import { useSearchFrameworks } from "lib/hooks/api/frameworks/useSearchFrameworks";
import { SearchRequestSortOrder } from "lib/types/graphQLEnums";
import { Select, SelectProps } from "./Inputs";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options">;

const DEFAULT_FRAMEWORK_SELECT_FILTERS = {
  sort: "title",
  limit: 10,
  page: 1,
  sortOrder: SearchRequestSortOrder.Asc,
};

export function FrameworkSelect<T extends FieldValues>(props: FieldProps<T>) {
  const { field } = useController(props);

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedText] = useDebouncedValue(searchText, 300);

  const hasSelection = !!(field.value as string[])?.length;

  // Resolve labels for the currently-selected framework IDs. Reactive to
  // field.value (the query key includes it), enabled independently of the
  // live search query, uncapped for the selection.
  const { data: initialFrameworks } = useSearchFrameworks(
    {
      frameworkIds: field.value,
      ...DEFAULT_FRAMEWORK_SELECT_FILTERS,
      limit: (field.value as string[])?.length || DEFAULT_FRAMEWORK_SELECT_FILTERS.limit,
    },
    false,
    {
      enabled: hasSelection,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const { data, isLoading } = useSearchFrameworks(
    {
      searchText: debouncedText,
      ...DEFAULT_FRAMEWORK_SELECT_FILTERS,
    },
    false,
    {
      enabled: debouncedText.length > 0,
    },
  );

  // Dropdown options = live search results, plus any selected frameworks not
  // already present (to supply their label), deduped by id. Guarantees every
  // selected value has a matching option so antd never shows the raw GUID.
  const liveOptions = (data?.results ?? []).map((framework) => ({
    label: framework.title,
    value: framework.id,
  }));
  const seenOptionValues = new Set(liveOptions.map((option) => option.value));
  const selectedLabelOptions = (initialFrameworks?.results ?? [])
    .filter((framework) => !seenOptionValues.has(framework.id))
    .map((framework) => ({ label: framework.title, value: framework.id }));
  const options = [...liveOptions, ...selectedLabelOptions];

  // TODO: Add paywall here when data type for frameworks is created
  return (
    <RedactedWrapper
      redactContent={
        <Select
          {...props}
          selectAll={!!debouncedText}
          loading={isLoading && !!debouncedText}
          options={options}
          optionFilterProp="label"
          onSearch={(val) => setSearchText(val)}
          showSearch
          filterOption={false}
          disabled
          enablePortal={props.enablePortal}
        />
      }
      contextSource="Framework filter"
    >
      <Select
        {...props}
        selectAll={!!debouncedText}
        loading={isLoading && !!debouncedText}
        options={options}
        optionFilterProp="label"
        onSearch={(val) => setSearchText(val)}
        showSearch
        filterOption={false}
        enablePortal
      />
    </RedactedWrapper>
  );
}
